import type { AccountInfo } from '@azure/msal-browser';
import { store } from '../../store';
import {
  checkNetworkStatus,
  networkStatusAtom,
} from '../../store/network/networkState';
import { logger } from '../../utils/logger';
import { flushDraftsBeforeRedirect, msalInstance } from '../azureAuthService';
import {
  createMobileLoginRequest,
  createSilentTokenRequest,
  isInteractionRequiredError,
} from './loginRequestBuilder';

type TimerHandle = ReturnType<typeof setTimeout>;

export interface SessionDeps {
  auth: {
    getAccount(): AccountInfo | null;
    acquireTokenSilent(account: AccountInfo): Promise<string>;
    redirectToSignIn(): Promise<void>;
  };
  storage: {
    getToken(): string | null;
    setToken(token: string): void;
  };
  now: () => number;
  setTimer: (fire: () => void, ms: number) => TimerHandle;
  clearTimer: (timer: TimerHandle) => void;
  connectivity: {
    isOnline(): boolean;
    subscribe(listener: () => void): () => void;
    check(): Promise<boolean>;
  };
  focus: { subscribe(listener: () => void): () => void };
  flushDrafts: () => Promise<void>;
}

export type SessionStatus = 'usableToken' | 'offline' | 'signedOut';

export type AccessDecision = 'allow' | 'login' | 'signInRedirect';

export interface SessionClaims {
  name: string | null;
  email: string | null;
  /** Milliseconds since the epoch, or null when the token carries no `exp`. */
  expiresAt: number | null;
}

const RENEW_BEFORE_EXPIRY_MS = 5 * 60_000;
const RETRY_RENEWAL_MS = 5 * 60_000;
const MIN_FOCUS_CHECK_GAP_MS = 60_000;

const decodePayload = (token: string): Record<string, unknown> | null => {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
    const payload: unknown = JSON.parse(new TextDecoder().decode(bytes));
    return typeof payload === 'object' && payload !== null
      ? (payload as Record<string, unknown>)
      : null;
  } catch {
    // A token the app cannot read has no claims. The parse error is not logged: it can
    // quote the payload, and with it the username.
    logger.warn('Stored token could not be decoded');
    return null;
  }
};

const nonEmptyString = (value: unknown) =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

const nameFromEmail = (email: string) =>
  email
    .split('@')[0]
    .split(/[._]/)
    .filter(Boolean)
    .map((part) => part[0].toUpperCase() + part.slice(1))
    .join(' ') || null;

const readClaims = (token: string): SessionClaims | null => {
  const payload = decodePayload(token);
  if (!payload) return null;
  const given = nonEmptyString(payload.given_name);
  const family = nonEmptyString(payload.family_name);
  const email = nonEmptyString(payload.email);
  const name =
    given && family
      ? `${given} ${family}`
      : (nonEmptyString(payload.name) ?? (email ? nameFromEmail(email) : null));
  const exp = payload.exp;
  return {
    name,
    email,
    expiresAt: typeof exp === 'number' ? exp * 1000 : null,
  };
};

export function createSessionModule(deps: SessionDeps) {
  const { storage } = deps;
  let timer: TimerHandle | null = null;
  let unsubscribes: (() => void)[] = [];
  const listeners = new Set<() => void>();
  let lastStatus: SessionStatus | null = null;
  // An online silent renewal said only the user can fix the sign-in.
  let userNeeded = false;
  let lastFocusCheck = 0;
  let userSignedOut = false;
  // Renewal runs in the background only between start() and stop().
  let started = false;

  // After sign-out the sign-in library may still list the account until it is cleared.
  const account = () => (userSignedOut ? null : deps.auth.getAccount());

  const status = (): SessionStatus => {
    if (!account() || !storage.getToken()) return 'signedOut';
    if (!deps.connectivity.isOnline()) return 'offline';
    return userNeeded ? 'signedOut' : 'usableToken';
  };

  const notifyIfChanged = () => {
    const current = status();
    if (current === lastStatus) return;
    lastStatus = current;
    listeners.forEach((listener) => listener());
  };

  const clearRenewal = () => {
    if (timer !== null) deps.clearTimer(timer);
    timer = null;
  };

  const scheduleRenewal = (ms: number) => {
    clearRenewal();
    if (!started || userSignedOut) return;
    timer = deps.setTimer(() => {
      timer = null;
      renewInBackground();
    }, ms);
  };

  const scheduleFromToken = (token: string) => {
    const expiresAt = readClaims(token)?.expiresAt;
    scheduleRenewal(
      expiresAt == null
        ? RETRY_RENEWAL_MS
        : Math.max(0, expiresAt - RENEW_BEFORE_EXPIRY_MS - deps.now()),
    );
  };

  // Never redirects and never clears the stored sign-in, whatever the failure.
  // Resolves to the failure, if any, so background callers need not catch.
  const renew = async (account: AccountInfo): Promise<unknown> => {
    try {
      const token = await deps.auth.acquireTokenSilent(account);
      if (userSignedOut) return new Error('Signed out during token renewal');
      storage.setToken(token);
      userNeeded = false;
      scheduleFromToken(token);
      return null;
    } catch (error) {
      if (isInteractionRequiredError(error) && deps.connectivity.isOnline()) {
        userNeeded = true;
        logger.warn('Token renewal needs the user to sign in again');
      } else {
        logger.warn(
          'Token renewal failed, keeping the session: ' +
            (error instanceof Error ? error.message : String(error)),
        );
      }
      scheduleRenewal(RETRY_RENEWAL_MS);
      return error ?? new Error('Token renewal failed');
    } finally {
      notifyIfChanged();
    }
  };

  const renewInBackground = () => {
    const signedIn = account();
    if (!signedIn) return;
    if (!deps.connectivity.isOnline()) {
      scheduleRenewal(RETRY_RENEWAL_MS);
      return;
    }
    void renew(signedIn);
  };

  // Coming back to the app (camera, another app) checks the token, but only online:
  // offline there is nothing to renew against (AU-2).
  const onFocus = () => {
    if (!deps.connectivity.isOnline()) return;
    const now = deps.now();
    if (now - lastFocusCheck < MIN_FOCUS_CHECK_GAP_MS) return;
    lastFocusCheck = now;
    const token = storage.getToken();
    const expiresAt = token ? readClaims(token)?.expiresAt : null;
    if (
      token &&
      (expiresAt == null || now >= expiresAt - RENEW_BEFORE_EXPIRY_MS)
    ) {
      renewInBackground();
    }
  };

  const refresh = async (): Promise<void> => {
    const signedIn = account();
    if (!signedIn) {
      throw new Error('No signed-in account to renew the token for');
    }
    const failure = await renew(signedIn);
    if (failure) throw failure;
  };

  return {
    /** The stored token as it is, even when expired: renewal is the timer's and the 401 retry's job. */
    getToken: () => storage.getToken(),

    claims(): SessionClaims | null {
      const token = storage.getToken();
      return token ? readClaims(token) : null;
    },

    /** Forces a silent renewal; rejects when it fails, the user is needed included. */
    refresh,

    status,

    /**
     * The route guard's answer. No account or no stored token goes to the login screen,
     * online or offline. Offline with a stored sign-in always opens the app.
     * Online, when a silent renewal said the user is needed, it saves the field drafts and
     * redirects to Microsoft; local data and the stored sign-in stay on the device. A redirect
     * the sign-in library refuses allows the app, so the next navigation tries again.
     */
    async checkAccess(): Promise<AccessDecision> {
      const online = await deps.connectivity.check();
      if (!account() || !storage.getToken()) return 'login';
      if (!online || !userNeeded) return 'allow';
      await deps.flushDrafts();
      try {
        await deps.auth.redirectToSignIn();
      } catch (error) {
        // A refused redirect (another sign-in in progress, the connection gone) must not
        // take the screen away: the next navigation tries again.
        logger.warn(
          'Redirect to sign in failed, staying in the app: ' +
            (error instanceof Error ? error.message : String(error)),
        );
        return 'allow';
      }
      return 'signInRedirect';
    },

    /**
     * A token from a Microsoft sign-in: the redirect return, and the auth service's start-up with a
     * token already stored. Clears any earlier sign-out. The login screen renews through `refresh()`.
     */
    recordSignIn(token: string) {
      storage.setToken(token);
      userSignedOut = false;
      userNeeded = false;
      scheduleFromToken(token);
      notifyIfChanged();
    },

    /** Logout: stops renewal. Clearing the sign-in library's items stays with logout. */
    recordSignOut() {
      userSignedOut = true;
      clearRenewal();
      notifyIfChanged();
    },

    onChange(listener: () => void): () => void {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    start() {
      if (started) return;
      started = true;
      lastStatus = status();
      lastFocusCheck = deps.now();
      unsubscribes = [
        deps.focus.subscribe(onFocus),
        deps.connectivity.subscribe(notifyIfChanged),
      ];
      const token = storage.getToken();
      if (token) scheduleFromToken(token);
    },

    stop() {
      started = false;
      unsubscribes.forEach((unsubscribe) => unsubscribe());
      unsubscribes = [];
      clearRenewal();
    },
  };
}

// Every dependency is looked up when called, not here, so importing this file runs nothing,
// and tests that spy on the shared msalInstance see the calls.
export const session = createSessionModule({
  auth: {
    getAccount: () => msalInstance.getAllAccounts()[0] ?? null,
    acquireTokenSilent: async (account) => {
      const request = createSilentTokenRequest(account);
      request.forceRefresh = true;
      return (await msalInstance.acquireTokenSilent(request)).accessToken;
    },
    // Not loginRedirect: that clears the sign-in library's stored items first.
    redirectToSignIn: () =>
      msalInstance.acquireTokenRedirect(createMobileLoginRequest()),
  },
  storage: {
    getToken: () => localStorage.getItem('jwtToken'),
    setToken: (token) => localStorage.setItem('jwtToken', token),
  },
  now: () => Date.now(),
  setTimer: (fire, ms) => setTimeout(fire, ms),
  clearTimer: (timer) => clearTimeout(timer),
  connectivity: {
    isOnline: () => store.get(networkStatusAtom),
    subscribe: (listener) => store.sub(networkStatusAtom, listener),
    check: () => checkNetworkStatus(),
  },
  focus: {
    subscribe: (listener) => {
      window.addEventListener('focus', listener);
      return () => window.removeEventListener('focus', listener);
    },
  },
  flushDrafts: () => flushDraftsBeforeRedirect(),
});
